import { useNavigate } from "react-router-dom";
import { 
    useDeleteDagRunEndpointJobsDelete,
    useDeleteDataFileDataFileDataFileHashDelete, 
    useDeleteDatasetDatasetDatasetHashDelete, 
    useDeleteModelFamilyModelFamilyModelFamilyIdDelete, 
    useDeleteReadacrossReadacrossReadacrossIdDelete, 
    useDeleteResultsetResultsetResultsetIdDelete
} from "../../api/generated/fastAPI";

const useDelete = ({
    setPopupMessage,
    refetchFn,
}: {
    setPopupMessage: (message: string, isSuccess: boolean) => void,
    refetchFn?: () => void,
}) => {
    const navigate = useNavigate();

    const deleteFileMutation = useDeleteDataFileDataFileDataFileHashDelete();
    const deleteDatasetsMutation = useDeleteDatasetDatasetDatasetHashDelete();
    const deleteModelFamilyMutation = useDeleteModelFamilyModelFamilyModelFamilyIdDelete();
    const deleteResultsetMutation = useDeleteResultsetResultsetResultsetIdDelete();
    const deleteRAMutation = useDeleteReadacrossReadacrossReadacrossIdDelete();
    const deleteJobsMutation = useDeleteDagRunEndpointJobsDelete();

    const deleteFiles = async (ids: (string | number | { dag_id: string, run_id: string })[]) => {
        const promises = ids.map(async (id) => {
            return deleteFileMutation.mutateAsync({ dataFileHash: id as string });
        });
        
        try {
            await Promise.all(promises);
            setPopupMessage(`You've successfully delete the selected ${ids.length > 1 ? 'files' : 'file'}`, true);
            refetchFn ? refetchFn() : navigate('/files');
        } catch (err:any) {
            console.log(err);
            setPopupMessage(`Something went wrong while deleting the selected ${ids.length > 1 ? 'files' : 'file'}. Please try again.`, false);
        }
    };

    const deleteDatasets = async (ids: (string | number | { dag_id: string, run_id: string })[]) => {
        const promises = ids.map(async (id) => {
            return deleteDatasetsMutation.mutateAsync({ datasetHash: id as string });
        });
        
        try {
            await Promise.all(promises);
            setPopupMessage(`You've successfully delete the selected ${ids.length > 1 ? 'datasets' : 'dataset'}`, true);
            refetchFn ? refetchFn() : navigate('/datasets');
        } catch (err:any) {
            console.log(err);
            setPopupMessage(`Something went wrong while deleting the selected ${ids.length > 1 ? 'datasets' : 'dataset'}. Please try again.`, false);
        }
    };

    const deleteModelFamilies = async (ids: (string | number | { dag_id: string, run_id: string })[]) => {
        const promises = ids.map(async (id) => {
            return deleteModelFamilyMutation.mutateAsync({ modelFamilyId: Number(id) });
        });
        
        try {
            await Promise.all(promises);
            setPopupMessage(`You've successfully delete the selected ${ids.length > 1 ? 'model families' : 'model family'}`, true);
            refetchFn ? refetchFn() : navigate('/models');
        } catch (err:any) {
            console.log(err);
            setPopupMessage(`Something went wrong while deleting the selected ${ids.length > 1 ? 'model families' : 'model family'}. Please try again.`, false);
        }
    };

    const deleteResultsets = async (ids: (string | number | { dag_id: string, run_id: string })[]) => {
        const promises = ids.map(async (id) => {
            return deleteResultsetMutation.mutateAsync({ resultsetId: Number(id) });
        });
        
        try {
            await Promise.all(promises);
            setPopupMessage(`You've successfully delete the selected ${ids.length > 1 ? 'resultsets' : 'resultset'}`, true);
            refetchFn ? refetchFn() : navigate('/resultsets');
        } catch (err:any) {
            console.log(err);
            setPopupMessage(`Something went wrong while deleting the selected ${ids.length > 1 ? 'resultsets' : 'resultset'}. Please try again.`, false);
        }
    };

    const deleteRAs = async (ids: (string | number | { dag_id: string, run_id: string })[]) => {
        const promises = ids.map(async (id) => {
            return deleteRAMutation.mutateAsync({ readacrossId: Number(id) });
        });
        
        try {
            await Promise.all(promises);
            setPopupMessage(`You've successfully delete the selected ${ids.length > 1 ? 'read-across predictions' : 'read-across prediction'}`, true);
            refetchFn ? refetchFn() : navigate('/readacross');
        } catch (err:any) {
            console.log(err);
            setPopupMessage(`Something went wrong while deleting the selected ${ids.length > 1 ? 'read-across predictions' : 'read-across prediction'}. Please try again.`, false);
        }
    };

    const deleteJobs = async (ids: (string | number | { dag_id: string, run_id: string })[]) => {
        const promises = ids.map(async (id) => {
            const { dag_id, run_id } = id as { dag_id: string, run_id: string };
            return deleteJobsMutation.mutateAsync({ params: { dag_id: encodeURIComponent(dag_id), run_id: encodeURIComponent(run_id) } });
        });
        
        try {
            await Promise.all(promises);
            setPopupMessage(`You've successfully delete the selected ${ids.length > 1 ? 'jobs' : 'job'}`, true);
            refetchFn ? refetchFn() : navigate('/jobs');
        } catch (err:any) {
            console.log(err);
            setPopupMessage(`Something went wrong while deleting the selected ${ids.length > 1 ? 'jobs' : 'job'}. Please try again.`, false);
        }
    };


    return {
        deleteFiles,
        deleteDatasets,
        deleteModelFamilies,
        deleteResultsets,
        deleteRAs,
        deleteJobs,
    }
};

export default useDelete;